import { FetchError } from 'ofetch';

export interface APIResult<T> {
    success: boolean;
    statusCode: number;
    message: string;
    data: T;
}


export class API {

    private static getFetch() {
        const { $apiFetch } = useNuxtApp();
        return $apiFetch;
    }

    static async get<T>(url: string, suppressError: boolean = false): Promise<APIResult<T>> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, { method: 'GET' });
            return {
                success: true,
                statusCode: result.statusCode,
                message: result.message,
                data: result.data
            };
        } catch (err: any) {
            const error: FetchError = err as FetchError;
            if (suppressError) {
                console.error("GET Error:", err);
            }
            return {
                success: false,
                statusCode: err.response?.status || 500,
                message: err.data?.message || "An unexpected error occurred",
                data: null as any
            };
        }
    }

    static async post<T>(url: string, data: any, suppressError: boolean = false): Promise<APIResult<T>> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, {
                method: 'POST',
                body: data
            });
            return {
                success: true,
                statusCode: result.statusCode,
                message: result.message,
                data: result.data
            };
        } catch (err: any) {
            const error: FetchError = err as FetchError;
            if (suppressError) {
                console.error("POST Error:", err);
            }
            return {
                success: false,
                statusCode: err.response?.status || 500,
                message: err.data?.message || "An unexpected error occurred",
                data: null as any
            };
        }
    }

    static async postWithError<T>(url: string, data: any): Promise<[T | null, any]> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, {
                method: 'POST',
                body: data
            });
            return [result.data, null];
        } catch (err) {
            const error = err as FetchError;
            return [null, error.response];
        }
    }

    static async postForm<T>(url: string, data: FormData): Promise<T | null> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, {
                method: 'POST',
                body: data
            });
            return result.data;
        } catch (err) {
            throw err;
        }
    }

    static async put<T>(url: string, data: any): Promise<T | null> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, {
                method: 'PUT',
                body: data
            });
            return result.data;
        } catch (err) {
            throw err;
        }
    }

    static async delete<T>(url: string): Promise<T | null> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, { method: 'DELETE' });
            return result.data;
        } catch (err) {
            throw err;
        }
    }

    static async deleteWithResult<T>(url: string): Promise<APIResult<T>> {
        try {
            const result = await this.getFetch()<APIResult<T>>(url, { method: 'DELETE' });
            return {
                success: true,
                statusCode: result.statusCode,
                message: result.message,
                data: result.data
            };
        } catch (err: any) {
            return {
                success: false,
                statusCode: err.response?.status || 500,
                message: err.data?.message || "Failed to delete",
                data: null as any
            };
        }
    }
}